import React from "react";
import { Form, InputGroup } from "react-bootstrap";
import Select from 'react-select';

import FormBuilderInputAutolookup from "./FormBuilderInputAutolookup";
import "./FormBuilderInput.scss";

export default function FormBuilderInput(input) {
	const [value, setValue] = React.useState(input.value || "");
	const inputRef = React.useRef();

	const onChange = e => {
		let value = e.target.value;

		// Normalize
		if (Array.isArray(value)) {
			value = [...value];
			value = value.map(v => (typeof v === "object" ? v.value : v));
		} else if (typeof value === 'object' && value !== null) {
			value = value.value;
		}

		setValue(e.target.value);
		input.onChange(value);

		// Execute custom onchange
		if (input.onchange) {
			const temp_ob = {
				...input,
				value: value,
				form: e.target.form,
				wrapper: inputRef.current
			};
			new Function("input", "value", "form", input.onchange)
				.call(temp_ob, e.target, e.target.value, e.target.form);
		}
	};
	const props = {};
	["min", "max", "required", "placeholder"]
		.filter((prop) => input[prop] !== undefined)
		.forEach((prop) => (props[prop] = input[prop]));

	let menuItems = false;
	const selectTypes = ['select', 'multi-select'];
	if (selectTypes.includes(input.type)) {
		delete props.type;
		// props.select = true;

		const normalizeMenuItem = (value) => {
			if (typeof value !== "object") {
				return [value, value];
			}
			if (value.length === 1) {
				return [value[0], value[0]];
			}
			return value;
		};
		const generateMenuItem = (value, index) => {
			value = normalizeMenuItem(value);
			return ({ value: value[0], label: value[1] || value[0], isDivider: value[0] === '-' });
		};
		if (!input.values.length) {
			input.values = [];
		}
		(input.values[0]?.[0] != '') && input.values.unshift(['', '']);

		menuItems = input?.values?.map ? input.values.map(generateMenuItem) : [];
	}

	if (["textarea"].includes(input.type)) {
		props.as = "textarea";
		props.rows = 5;
	}
	const typeMap = {
		undefined: "text",
		string: "text",
	};

	let type = typeMap[input.type] || input.type || "text";
	if (input.is_hidden) {
		type = "hidden";
	}
	if (type) {
		props.type = type;
	}
	props.id = input.id || "";
	props.label = input.label || "";
	props.placeholder = input.label || "";
	props.value = value;
	props.required = input.is_required;
	props.disabled = input.is_locked;
	props.size = "lg";

	if (['int', 'decimal'].includes(props.type)) {
		props.type = 'number';
	}
	let baseInput;

	// Custom floating label for select
	const FloatingLabelSelect = ({ label, value, isMulti, options, ...rest }) => {
		const [isFocused, setIsFocused] = React.useState(false);
		// react-select expects value to be an array for multi-select, or option object for single
		const hasValue = isMulti ? Array.isArray(value) && value.length > 0 : !!value;
		rest.placeholder = '';
		return (
			<div className={[
				`floating-label-select`,
				(isFocused || hasValue) ? ' floating' : '',
			].filter(Boolean).join(" ")}>
				<label className="floating-label">{label}</label>
				<div className="floating-select-wrapper">
					<Select
						isMulti={isMulti}
						options={options}
						value={value}
						{...rest}
						onChange={e => onChange({ target: { value: e } })}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
					/>
				</div>
			</div>
		);
	};

	if (input.type === "static") {
		baseInput = (
			<InputGroup ref={inputRef} className="mt-1" data-input-type="text">
				<label>{props.label}</label>
				<div className="text value" ref={inputRef} dangerouslySetInnerHTML={{ __html: input.value || '' }} />
			</InputGroup>
		);
	} else if (type === "autolookup") {
		props.object = input.object;
		props.filters = input.filters;
		baseInput = <FormBuilderInputAutolookup innerRef={inputRef} {...props} onChange={onChange} />;
	} else if (input.type === "checkbox" || input.type === "radio") {
		baseInput = (
			<InputGroup ref={inputRef} className="mt-1">
				<Form.Check {...props} onChange={onChange} />
			</InputGroup>
		);
	} else {
		['decimal', 'int'].includes(input.type) && (props.type = "number");
		['date', 'datetime-local'].includes(input.type) && (props.type = "date");
		['time'].includes(input.type) && (props.type = "time");
		baseInput = (
			<InputGroup ref={inputRef} className="mt-1">
				{input.prefix && <InputGroup.Text>{input.prefix}</InputGroup.Text>}
				{!selectTypes.includes(input.type) && (
					<Form.FloatingLabel label={props.label}>
						<Form.Control {...props} required onChange={onChange} />
					</Form.FloatingLabel>
				)}
				{selectTypes.includes(input.type) && (
					<FloatingLabelSelect
						label={props.label}
						value={value}
						isMulti={input.type === "multi-select"}
						options={menuItems}
						{...props}
						onChange={option => {
							// For multi-select, value is array; for single, value is object
							setValue(option);
							input.onChange(option);
						}}
					/>
				)}
				{input.suffix && <InputGroup.Text>{input.suffix}</InputGroup.Text>}
			</InputGroup>
		);
	}

	const hasRequiredWarning =
		props.required && [undefined, null, ""].includes(value)
			? "has-warning"
			: "";
	const isEmailValid = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
	const error =
		input.type === "email" &&
		!isEmailValid(value) &&
		"Please enter a valid email address";

	return (
		<div
			key={props.key || input.id}
			className={
				"form-builder-input" +
				(props.required ? " is-required" : "") +
				(hasRequiredWarning ? " has-warning" : "") +
				(props.type === "hidden" ? " hidden" : "")
			}
			data-id={props.id}
		>
			{baseInput}
			{hasRequiredWarning && <div className="warning">Required Field</div>}
			{error && <div className="warning">{error}</div>}
		</div>
	);
}
